// src/common/components/cartModal/components/cartStage/CartStage.tsx
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MdDeleteOutline, MdPhone } from "react-icons/md";
import { RootState } from "../../../../../store/store";
import {
  clearCart,
  decreaseQuantity,
  increaseQuantity,
  removeFromCart,
} from "../../../../../store/slices/cartSlice";
import { selectCurrentUser } from "../../../../../store/slices/currentUserSlice";
import { selectIsOnline } from "../../../../../store/slices/networkSlice";
import { useTheme } from "../../../../hooks/useTheme";
import { CartItem } from "../../../../types/types";
import styles from "./CartStage.module.scss";
import { ROUTES } from "../../../../utils/constants";
import { getImageUri } from "../../../../utils/functions/getImageUri";
import Button from "../../../button/Button";
import { selectCurrentCluster } from "../../../../../store/slices/currentClusterSlice";
import { unitTypeToShort } from "../../../../utils/functions/enumToText";
import CutlerySelector from "../cutlerySelector/CutlerySelector";

interface CartStageProps {
  onClose: () => void;
  onCheckout: () => void;
  getSaleItemName: (id: number) => string;
  cutleryCount: number;
  incrementCutlery: () => void;
  decrementCutlery: () => void;
}

export default function CartStage({
  onClose,
  onCheckout,
  getSaleItemName,
  cutleryCount,
  incrementCutlery,
  decrementCutlery,
}: CartStageProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { items, total } = useSelector((state: RootState) => state.cart);
  const currentUser = useSelector(selectCurrentUser);
  const currentCluster = useSelector(selectCurrentCluster);
  const isOnline = useSelector(selectIsOnline);

  const showCutlery = !!currentCluster?.cutlery;

  const handleCall = () => {
    const phone = currentCluster?.phone_number;
    if (phone) {
      window.location.href = `tel:${phone.replace(/\D/g, "")}`;
    }
  };

  const handleGoToItems = () => {
    onClose();
    navigate(ROUTES.MAIN);
  };

  const handleContinueShopping = () => {
    onClose();
    navigate(ROUTES.MAIN);
  };

  const handleClearCart = () => {
    dispatch(clearCart());
    onClose();
    navigate(ROUTES.MAIN);
  };

  if (items.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title} style={{ color: theme.text }}>
            Корзина
          </h2>
        </div>

        <div className={styles.emptyContent}>
          <h3 className={styles.emptyTitle} style={{ color: theme.primary }}>
            Корзина пуста
          </h3>
          <p
            className={styles.emptyText}
            style={{ color: theme.textSecondary }}
          >
            Добавьте товары из каталога, чтобы сделать заказ
          </p>
          <Button
            title="Перейти в каталог"
            onClick={handleGoToItems}
            variant="primary"
            className={styles.actionButton}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title} style={{ color: theme.text }}>
          Корзина
        </h2>
        <button className={styles.clearButton} onClick={handleClearCart}>
          <MdDeleteOutline
            size={18}
            color={theme.danger}
            className={styles.clearButtonIcon}
          />
          <span
            className={styles.clearButtonText}
            style={{ color: theme.danger }}
          >
            Очистить
          </span>
        </button>
      </div>

      {showCutlery && (
        <div className={styles.cutleryWrapper}>
          <CutlerySelector
            value={cutleryCount}
            onIncrement={incrementCutlery}
            onDecrement={decrementCutlery}
            disabledBackground={!isOnline}
          />
        </div>
      )}

      <div className={styles.itemsScrollView}>
        {items.map((item: CartItem) => {
          const itemTotal = item.price * item.quantity;
          const unitText =
            item.unit !== undefined && item.unit !== -1
              ? unitTypeToShort(item.unit)
              : "";

          return (
            <div
              key={item.id}
              className={styles.itemContainer}
              style={{
                backgroundColor: theme.card,
                borderColor: theme.border,
              }}
            >
              <img
                className={styles.itemImage}
                src={`${getImageUri()}${item.image}`}
                alt={item.isGift ? item.name : getSaleItemName(Number(item.id))}
              />

              <div className={styles.itemInfo}>
                <h4 className={styles.itemName} style={{ color: theme.text }}>
                  {item.isGift ? item.name : getSaleItemName(Number(item.id))}
                </h4>

                {item.quantity > 1 && (
                  <span
                    className={styles.unitPrice}
                    style={{ color: theme.textSecondary }}
                  >
                    {Number(item.price).toLocaleString("ru-RU")} ₽
                    {unitText && ` / ${unitText}`}
                  </span>
                )}

                <span
                  className={styles.itemTotal}
                  style={{ color: theme.primary }}
                >
                  {Number(itemTotal).toLocaleString("ru-RU")} ₽
                </span>
              </div>

              <div
                className={styles.quantityControls}
                style={{
                  borderColor: theme.border,
                  backgroundColor: isOnline ? theme.background : theme.card,
                }}
              >
                <button
                  className={styles.quantityButton}
                  onClick={() => dispatch(decreaseQuantity(item.id))}
                >
                  <span style={{ color: theme.primary }}>-</span>
                </button>

                <span
                  className={styles.quantityText}
                  style={{ color: theme.text }}
                >
                  {item.quantity}
                </span>

                <button
                  className={styles.quantityButton}
                  onClick={() => dispatch(increaseQuantity(item.id))}
                >
                  <span style={{ color: theme.success }}>+</span>
                </button>
              </div>

              <button
                className={styles.deleteButton}
                onClick={() => dispatch(removeFromCart(item.id))}
              >
                <span
                  className={styles.deleteButtonText}
                  style={{ color: theme.danger }}
                >
                  ×
                </span>
              </button>
            </div>
          );
        })}
      </div>

      <div
        className={styles.footer}
        style={{ backgroundColor: `${theme.background}F2` }}
      >
        <div className={styles.totalsContainer}>
          <div className={styles.totalRow}>
            <span
              className={styles.totalLabel}
              style={{ color: theme.textSecondary }}
            >
              Сумма товаров:
            </span>
            <span className={styles.totalAmount} style={{ color: theme.text }}>
              {Number(total).toLocaleString("ru-RU")} ₽
            </span>
          </div>

          <div
            className={styles.finalTotalRow}
            style={{ borderTopColor: theme.border }}
          >
            <span
              className={styles.finalTotalLabel}
              style={{ color: theme.text }}
            >
              Итого к оплате:
            </span>
            <span
              className={styles.finalTotalAmount}
              style={{ color: theme.primary }}
            >
              {Number(total).toLocaleString("ru-RU")} ₽
            </span>
          </div>
        </div>

        <div className={styles.buttonsContainer}>
          {isOnline ? (
            <Button
              title={currentUser ? "Оформить заказ" : "Войти и оформить заказ"}
              onClick={onCheckout}
              variant="primary"
              className={styles.actionButton}
              fullWidth
            />
          ) : currentCluster?.phone_number ? (
            <Button
              title="Нет интернета, нажмите чтобы позвонить"
              onClick={handleCall}
              variant="primary"
              className={styles.actionButton}
              icon={<MdPhone size={18} />}
              fullWidth
            />
          ) : (
            <Button
              title="Нужен интернет для оформления заказа"
              onClick={onCheckout}
              variant="primary"
              className={styles.actionButton}
              disabled
              fullWidth
            />
          )}

          <Button
            title="Продолжить покупки"
            onClick={handleContinueShopping}
            variant="secondary"
            className={styles.actionButton}
            fullWidth
          />
        </div>
      </div>
    </div>
  );
}
